<template>
  <div class="pagination flex items-center justify-center space-x-1 py-4">
    <!-- 上一页 -->
    <button 
      class="pagination-btn"
      @click="handlePrevPage"
      :disabled="currentPage === 1"
      aria-label="Previous page"
    >
      <i class="fa fa-chevron-left"></i>
    </button>

    <!-- 固定数量的页码按钮（用displayCount控制） -->
    <button 
      v-for="i in displayCount" 
      :key="i"
      class="pagination-number"
      :class="{ 'active': currentPage === getPage(i), 'hidden': !getPage(i) }"
      @click="handlePageChange(getPage(i))"
    >
      {{ getPage(i) }}
    </button>
    
    <!-- 下一页 -->
    <button 
      class="pagination-btn"
      @click="handleNextPage"
      :disabled="currentPage === computedTotalPages"
      aria-label="Next page"
    >
      <i class="fa fa-chevron-right"></i>
    </button>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue';

const props = defineProps({
  // 基础分页参数（新增）
  total: { type: Number, required: true, default: 0 }, // 总条数
  pageSize: { type: Number, required: true, default: 10 }, // 每页条数
  
  // 原有参数（兼容）
  currentPage: { type: Number, required: true, default: 1 },
  totalPages: { type: Number, default: 0 }, // 改为非必填，优先级低于自动计算
  displayCount: { type: Number, default: 5 },
  
  // 可选配置
  showEmpty: { type: Boolean, default: true }, // 无数据时是否显示分页
  pageSizeOptions: { type: Array, default: () => [10, 20, 30, 50] } // 可选的每页条数（预留）
});

const emit = defineEmits(['page-change', 'page-size-change']); // 新增page-size-change事件

// 计算总页数（优先使用自动计算，兼容原有totalPages传入）
const computedTotalPages = computed(() => {
  // 如果总条数为0，返回0或1（根据showEmpty配置）
  if (props.total <= 0) {
    return props.showEmpty ? 1 : 0;
  }
  
  // 如果传入了totalPages且大于0，优先使用（兼容原有逻辑）
  if (props.totalPages > 0) {
    return props.totalPages;
  }
  
  // 自动计算总页数（向上取整）
  return Math.ceil(props.total / props.pageSize) || 1;
});

// 计算当前显示范围的起始页（优化偏移逻辑）
const startPage = computed(() => {
  const { currentPage, displayCount } = props;
  const totalPages = computedTotalPages.value;
  
  if (totalPages <= displayCount) return 1;

  const half = Math.floor(displayCount / 2);
  let start = currentPage - half;

  // 左边界
  if (start < 1) start = 1;
  // 右边界
  if (start + displayCount - 1 > totalPages) {
    start = totalPages - displayCount + 1;
  }
  return start;
});

// 获取第i个位置应显示的页码（超出范围返回null）
const getPage = (i) => {
  const page = startPage.value + i - 1;
  return page <= computedTotalPages.value ? page : null;
};

// 页码变更方法
const handlePageChange = (page) => {
  if (page && page !== props.currentPage && page >= 1 && page <= computedTotalPages.value) {
    emit('page-change', page);
  }
};

// 上一页
const handlePrevPage = () => {
  if (props.currentPage > 1) {
    emit('page-change', props.currentPage - 1);
  }
};

// 下一页
const handleNextPage = () => {
  if (props.currentPage < computedTotalPages.value) {
    emit('page-change', props.currentPage + 1);
  }
};

</script>

<style scoped>
.pagination {
  font-size: 14px;
}

/* 基础按钮样式（白底灰边） */
.pagination-btn,
.pagination-number {
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  border: 1px solid #d1d5db; /* 浅灰边框 */
  background-color: #ffffff; /* 白色背景 */
  color: #111827; /* 深黑文本 */
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

/* 悬停效果（浅灰背景） */
.pagination-btn:hover:not(:disabled),
.pagination-number:hover:not(.active) {
  background-color: #f3f4f6; /* 浅灰背景 */
  border-color: #9ca3af; /* 中灰边框 */
  color: #111827;
}

/* 禁用状态（浅灰文本） */
.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  color: #9ca3af;
}

/* 激活状态（黑底白字） */
.pagination-number.active {
  background-color: #111827; /* 黑色背景 */
  color: #ffffff; /* 白色文本 */
  border-color: #111827; /* 黑色边框 */
  font-weight: 500;
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
}

/* 移动端适配 */
@media (max-width: 640px) {
  .pagination-btn,
  .pagination-number {
    min-width: 30px;
    height: 30px;
    font-size: 12px;
  }
}

/* 超小屏幕适配 */
@media (max-width: 480px) {
  .pagination {
    flex-wrap: wrap;
    gap: 6px;
    padding: 4px;
  }
}

.hidden {
  display: none;
}

/* 强化过渡动画 */
.pagination-number {
  transition: all 0.2s ease-out; /* 更平滑的过渡 */
}

.pagination-number.active {
  transition: all 0.2s ease-in; /* 激活状态过渡 */
}

/* 无数据时隐藏分页 */
.pagination.hidden-pagination {
  display: none;
}
</style>